import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface LegacyState {
  active: boolean;
  deadMansSwitchDays: number;
  lastOwnerCheckin: number;
  activatedAt: number | null;
  daysSinceCheckin: number;
  switchTriggered: boolean;
  status: 'armed' | 'triggered' | 'active';
}

export function LegacyPane() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ legacy: LegacyState }>({
    queryKey: ['legacy'],
    queryFn: () => api('/api/legacy'),
  });
  const [days, setDays] = useState<number | null>(null);

  async function act(path: string, body?: object) {
    await api(`/api/legacy/${path}`, { method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}) });
    qc.invalidateQueries({ queryKey: ['legacy'] });
  }
  async function saveDays(d: number) {
    await api('/api/legacy', { method: 'PATCH', body: JSON.stringify({ deadMansSwitchDays: d }) });
    qc.invalidateQueries({ queryKey: ['legacy'] });
  }

  if (isLoading || !data) return <div className="text-ink/40 text-[12px] py-8">Loading…</div>;
  const L = data.legacy;
  const switchDays = days ?? L.deadMansSwitchDays;

  const statusColor = L.status === 'active' ? 'text-amber' : L.status === 'triggered' ? 'text-red-300' : 'text-emerald-300';
  const statusLabel = L.status === 'active' ? 'LEGACY MODE ACTIVE'
    : L.status === 'triggered' ? 'SWITCH TRIGGERED — awaiting activation' : 'ARMED — owner active';

  return (
    <>
      <div className="mb-4">
        <h3 className="font-serif text-2xl text-ink m-0">Legacy mode</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          The posthumous switch. While you check in, the vault stays private to you and your invited
          circle. If you fall silent past the threshold, a trustee can open it as a memorial others
          can converse with — your voice, kept.
        </p>
      </div>

      <div className={`rounded-xl border border-ink/15 p-4 mb-5`}>
        <div className={`text-[11px] ${statusColor}`}>{statusLabel}</div>
        <div className="grid grid-cols-3 gap-4 mt-3">
          <div>
            <div className="font-serif text-3xl text-ink">{L.daysSinceCheckin}</div>
            <div className="text-[11px] text-ink/40">days since check-in</div>
          </div>
          <div>
            <div className="font-serif text-3xl text-ink">{L.deadMansSwitchDays}</div>
            <div className="text-[11px] text-ink/40">switch threshold</div>
          </div>
          <div>
            <div className="font-serif text-3xl text-ink">{Math.max(0, L.deadMansSwitchDays - L.daysSinceCheckin)}</div>
            <div className="text-[11px] text-ink/40">days remaining</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[160px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
        <div className="text-[11px] text-ink/45">Check-in</div>
        <div>
          <button onClick={() => act('checkin')}
            className="px-4 py-2 rounded-full bg-emerald-400/15 text-emerald-200 text-[11.5px] hover:bg-emerald-400/25">
            I'm still here — check in
          </button>
          <span className="ml-3 font-serif italic text-[12px] text-ink/40">resets the timer (also done automatically when you log in)</span>
        </div>
      </div>

      <div className="grid grid-cols-[160px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
        <div className="text-[11px] text-ink/45">Switch threshold</div>
        <div className="flex items-center gap-3">
          <input type="number" min={1} max={3650} value={switchDays}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-24 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[13px] text-ink focus:border-ink/40 focus:outline-none" />
          <span className="font-serif italic text-[12px] text-ink/40">days of silence before the switch triggers</span>
          {days !== null && days !== L.deadMansSwitchDays && (
            <button onClick={() => { void saveDays(days); setDays(null); }}
              className="px-3 py-1.5 rounded-full bg-amber text-void text-[11px] hover:bg-particle">save</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-[160px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
        <div className="text-[11px] text-ink/45">Legacy mode</div>
        <div className="flex items-center gap-3">
          {L.active ? (
            <button onClick={() => act('deactivate')}
              className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/70 hover:text-ink">
              Deactivate (I'm back)
            </button>
          ) : (
            <button onClick={() => act('activate')}
              className="px-4 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle">
              Activate legacy mode
            </button>
          )}
          <span className="font-serif italic text-[12px] text-ink/40">
            owner or a trustee can activate; only the owner can deactivate
          </span>
        </div>
      </div>

      <div className="mt-4 px-3 py-2 border-l-2 border-amber bg-amber/[0.04] font-serif italic text-[13px] text-ink/65">
        Designate a trustee in the <span className="not-italic text-[11.5px] text-amber">People</span> tab
        (set someone's role to “trustee”). They'll be able to activate this when the time comes.
      </div>
    </>
  );
}
